import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Box,
  PlacementWithLogical,
  SystemStyleObject,
  Portal,
  MenuDivider,
  ChakraProps,
  chakra,
  Divider,
} from "@chakra-ui/react";
import { ReactComponent as More } from "@/assets/icons/more.svg";
import Icon from "@/components/Icon";
import React, { PropsWithChildren, ReactNode, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { OpMenuItem } from "../type";
import { genBlock } from "@/utils/bem";
import classNames from "classnames";
import { AlbumOperationMenuVariantType, albumOpStyle } from "../style";
import getScrollBarProps from "@/styles/scrollBarProps";
import { useTranslate } from "@/utils/lang";

interface Props {
  disabled?: boolean;
  onSelect: (key: string) => void;
  value?: string | number;
  onClose?(): void;
  onOpen?(): void;
  menuList: OpMenuItem[];
  staticList?: OpMenuItem[];
  placement?: PlacementWithLogical;
  // styleConfig?: ThemingProps<"Menu">["styleConfig"];
  styles?: {
    listRoot?: ChakraProps;
    menuList?: ChakraProps;
    menuButton?: ChakraProps;
    menuItem?: ChakraProps;
    staticMenuItem?: ChakraProps;
    buttonIcon?: ChakraProps;
    divider?: ChakraProps;
    itemSelected?: ChakraProps;
    scrollColor?: string;
  };
  variant?: AlbumOperationMenuVariantType;
  containerRef?: React.RefObject<HTMLElement | null>;
  menuListRef?: React.RefObject<HTMLDivElement>;
}

const block = genBlock("c-operation-menu");

function OperationMenu(props: PropsWithChildren<Props>) {
  const {
    onSelect,
    menuList,
    children,
    staticList = [],
    placement,
    // styleConfig,
    styles,
    value,
    disabled,
    variant,
    containerRef,
    onClose = () => null,
    onOpen = () => null,
    menuListRef,
  } = props;

  const { translate } = useTranslate();

  const onMenuSelect: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    const type = e.currentTarget.name;
    onSelect(type);
  };

  // const scrollStyle = {
  //   overflowY: "auto" as any,
  //   sx: {
  //     "&::-webkit-scrollbar": {
  //       w: "4px",
  //     },
  //     "&::-webkit-scrollbar-thumb": {
  //       "border-radius": "999px",
  //       bg: "transparent",
  //     },
  //     "&:hover::-webkit-scrollbar-thumb": {
  //       bg: styles?.scrollColor ?? "#5f5f5f",
  //     },
  //   },
  // };

  const scrollStyle = useMemo(() => {
    return getScrollBarProps({
      scrollBarColor: styles?.scrollColor ?? "#5f5f5f",
      scrollBarWidth: 4,
      scrollBarBorderRadius: 999,
      autoMargin: false,
    });
  }, [styles?.scrollColor]);

  const selectedItemStyle = useMemo(() => {
    return {
      color: "brand.primary",
      ...styles?.itemSelected,
    };
  }, [styles?.itemSelected]);

  return (
    <Menu
      placement={placement}
      styleConfig={albumOpStyle}
      variant={variant}
      onClose={onClose}
      onOpen={onOpen}
    >
      <MenuButton
        className={block("button")}
        disabled={disabled}
        onClick={(e) => e.stopPropagation()}
        {...styles?.menuButton}
      >
        {children ? (
          children
        ) : (
          <Box cursor="pointer">
            <Icon
              svg={More}
              fontSize="32px"
              color="fill.icon"
              fillbyCurrentColor
              strokeByCurrentColor
              {...styles?.buttonIcon}
            />
          </Box>
        )}
      </MenuButton>
      <Portal containerRef={containerRef}>
        <MenuList
          ref={menuListRef}
          rootProps={styles?.listRoot ?? { zIndex: 16 }}
          className={block("list")}
          {...scrollStyle}
          {...styles?.menuList}
        >
          {menuList.map((item) => {
            const content =
              typeof item.content === "string" ? translate(item.content) : item.content;
            const isSelected = typeof value !== "undefined" && value === item.key;
            return (
              <MenuItem
                className={classNames(item.className, block("item"))}
                name={item.key}
                key={item.key}
                onClick={onMenuSelect}
                {...styles?.menuItem}
                {...(isSelected ? selectedItemStyle : {})}
              >
                {content}
              </MenuItem>
            );
          })}
          {staticList?.length ? (
            <>
              <Box width="100%" pos="sticky" bottom="0">
                <Divider w="calc(100% - 18px)" mx="9px" {...styles?.divider}></Divider>
                {staticList?.map((item) => {
                  const content =
                    typeof item.content === "string" ? translate(item.content) : item.content;
                  return (
                    <MenuItem
                      className={classNames(item.className, block("item"))}
                      name={item.key}
                      key={item.key}
                      onClick={onMenuSelect}
                      {...styles?.staticMenuItem}
                    >
                      {content}
                    </MenuItem>
                  );
                })}
              </Box>
            </>
          ) : null}
        </MenuList>
      </Portal>
    </Menu>
  );
}

export default observer(OperationMenu);
